import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { environment } from '@orderna/admin-panel/src/environments/environment';
import { firstValueFrom, Observable } from 'rxjs';
import { IUser } from '../../model/user';
import { IUsersApiService } from './users-api.interface';
import { SuccessResponse } from '../shared/success-response';
import { FindUsersQueryDto } from './dtos/find-users.query.dto';
import { FindUsersResponseDto } from './dtos/find-users.response.dto';
import { FindUserNamesResponseDto } from './dtos/find-user-names.response.dto';
import { FindUserEditFormDataResponseDto } from './dtos/find-user-edit-form-data.response.dto';
import { HandleBlobHttpResponseOptions } from '@orderna/admin-panel/src/types/handle-blob-http-response-options';
import { handleBlobHttpResponse } from '@orderna/admin-panel/src/utils/handle-blob-http-response';
import { ExportUsersQueryDto } from './dtos/export-users.query.dto';
import { ExportExcelPdfService } from '../export-excel-pdf/export-excel-pdf.service';
import { CreateUserRequestDto } from './dtos/create-user.request.dto';
import { CreateUserResponseDto } from './dtos/create-user.response.dto';
import { UpdateUserRequestDto } from './dtos/update-user.request.dto';
import { UpdateUserResponseDto } from './dtos/update-user.response.dto';
import { FindUserOngoingRegisterSessionResponseDto } from './dtos/find-user-ongoing-register-session.response.dto';

let API_URL = `${environment.api}/users`;

@Injectable({
  providedIn: 'root',
})
export class UsersApiService implements IUsersApiService {
  private http = inject(HttpClient);
  private exportService = inject(ExportExcelPdfService);

  async createUser(data: CreateUserRequestDto): Promise<CreateUserResponseDto> {
    const response = await firstValueFrom(
      this.http.post<SuccessResponse<CreateUserResponseDto>>(
        `${API_URL}`,
        data,
      ),
    );

    return response.data;
  }

  async updateUser(
    userId: number,
    data: UpdateUserRequestDto,
  ): Promise<UpdateUserResponseDto> {
    const response = await firstValueFrom(
      this.http.patch<SuccessResponse<UpdateUserResponseDto>>(
        `${API_URL}/${userId}`,
        data,
      ),
    );

    return response.data;
  }

  deleteUser(userId: IUser['id']): Observable<any> {
    return this.http.delete(`${API_URL}/${userId}`);
  }

  deleteUsers(userIds: any): Observable<any> {
    return this.http.put(`${API_URL}/delete-many`, { userIds });
  }

  async findUsers(query: FindUsersQueryDto): Promise<FindUsersResponseDto> {
    const params = new HttpParams({ fromObject: query as any });
    const response = await firstValueFrom(
      this.http.get<SuccessResponse<FindUsersResponseDto>>(API_URL, {
        params,
      }),
    );

    return response.data;
  }

  async exportUsers(
    query: ExportUsersQueryDto,
    options?: HandleBlobHttpResponseOptions,
  ) {
    const params = new HttpParams({ fromObject: query as any });
    const response = await firstValueFrom(
      this.http.get(`${API_URL}/export`, {
        responseType: 'blob',
        observe: 'response',
        params,
      }),
    );

    handleBlobHttpResponse(response, options, this.exportService);
  }

  async findUserNames() {
    const response = await firstValueFrom(
      this.http.get<SuccessResponse<FindUserNamesResponseDto>>(
        `${API_URL}/names`,
      ),
    );

    return response.data;
  }

  async findUserEditFormData(userId: number) {
    const response = await firstValueFrom(
      this.http.get<SuccessResponse<FindUserEditFormDataResponseDto>>(
        `${API_URL}/${userId}/edit`,
      ),
    );

    return response.data;
  }

  async findCurrentUserOngoingRegisterSession() {
    const response = await firstValueFrom(
      this.http.get<SuccessResponse<FindUserOngoingRegisterSessionResponseDto>>(
        `${API_URL}/register-sessions/ongoing`,
      ),
    );

    return response.data;
  }
}
